import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import {
  createPlaidLinkToken,
  disconnectConnection,
  exchangePlaidToken,
  listConnections,
  snaptradeExchange,
  snaptradeLoginRedirect,
  syncConnection,
  type Connection,
} from "../lib/api-modules/integrations.js";
import { usePlaidLink } from "../lib/hooks/use-plaid-link.js";
import {
  Button,
  Card,
  EmptyState,
  Pill,
  SectionHeader,
} from "../components/design-system/index.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

/**
 * /integrations — Phase 8 Session 2.
 *
 * Lists external connections (Plaid v1; SnapTrade in 8.3) with last-sync
 * status + actions. "Connect a bank" walks Plaid Link → exchange → first
 * sync → toast. "Sync now" triggers an on-demand sync. "Disconnect" hits
 * Plaid itemRemove + DDB delete.
 */

export function IntegrationsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [linkInProgress, setLinkInProgress] = useState(false);
  const [snaptradeBusy, setSnaptradeBusy] = useState(false);
  const [snaptradeAwaitReturn, setSnaptradeAwaitReturn] = useState(false);

  const { data, error, isLoading } = useQuery({
    queryKey: ["integrations"],
    queryFn: listConnections,
  });
  const connections = data?.connections ?? [];

  const linkMutation = useMutation({ mutationFn: createPlaidLinkToken });
  const exchangeMutation = useMutation({
    mutationFn: exchangePlaidToken,
    onSuccess: (resp) => {
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      void queryClient.invalidateQueries({ queryKey: ["staging-transactions"] });
      toast.show(`Connected ${resp.institution_name}`);
    },
  });
  const snaptradeExchangeMutation = useMutation({
    mutationFn: snaptradeExchange,
    onSuccess: (resp) => {
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      void queryClient.invalidateQueries({
        queryKey: ["staging-transactions"],
      });
      setSnaptradeAwaitReturn(false);
      toast.show(
        resp.count === 0
          ? "No new brokerages found — already synced"
          : `Connected ${resp.count} brokerage${resp.count === 1 ? "" : "s"}`,
      );
    },
    onError: (e) => {
      setSnaptradeAwaitReturn(false);
      toast.show(`SnapTrade exchange failed: ${(e).message}`);
    },
  });
  const syncMutation = useMutation({
    mutationFn: (connectionId: string) => syncConnection(connectionId),
    onSuccess: (resp) => {
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      void queryClient.invalidateQueries({ queryKey: ["staging-transactions"] });
      toast.show(
        resp.status === "failure"
          ? `Sync failed: ${resp.error ?? "unknown error"}`
          : `${resp.added_count} new transactions added`,
      );
    },
  });
  const disconnectMutation = useMutation({
    mutationFn: (connectionId: string) => disconnectConnection(connectionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      toast.show("Connection removed");
    },
  });

  const plaidLink = usePlaidLink({
    onSuccess: (publicToken, institutionName, institutionId) => {
      setLinkInProgress(false);
      exchangeMutation.mutate({
        public_token: publicToken,
        institution_name: institutionName,
        ...(institutionId !== undefined ? { institution_id: institutionId } : {}),
      });
    },
    onExit: () => setLinkInProgress(false),
  });

  async function startConnect() {
    setLinkInProgress(true);
    try {
      const resp = await linkMutation.mutateAsync();
      await plaidLink.open(resp.link_token);
    } catch (e) {
      setLinkInProgress(false);
      toast.show(`Could not start Plaid Link: ${(e as Error).message}`);
    }
  }

  async function startSnaptradeConnect() {
    setSnaptradeBusy(true);
    try {
      const resp = await snaptradeLoginRedirect();
      // Open SnapTrade portal in a new tab. SnapTrade hosts the broker
      // picker + auth flow; the user returns to Hearth manually after
      // completing the flow at SnapTrade. This page exposes a "I just
      // finished — fetch new connections" button (snaptradeAwaitReturn).
      window.open(resp.redirect_uri, "_blank", "noopener,noreferrer");
      setSnaptradeAwaitReturn(true);
    } catch (e) {
      toast.show(
        `Could not start SnapTrade flow: ${(e as Error).message}`,
      );
    } finally {
      setSnaptradeBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      <SectionHeader
        title="Integrations"
        sub="Daily-sync banks, credit cards, loans, and brokerages. Hearth never auto-merges synced rows — they land in your staging inbox first."
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => {
                void startConnect();
              }}
              disabled={linkInProgress || linkMutation.isPending}
            >
              {linkInProgress ? "Connecting..." : "Connect a bank"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                void startSnaptradeConnect();
              }}
              disabled={snaptradeBusy}
            >
              {snaptradeBusy ? "Opening..." : "Connect a brokerage"}
            </Button>
          </div>
        }
      />

      <p className="mb-6 text-sm">
        <Link to="/integrations/staging" className="text-[var(--color-accent)] underline">
          Staging inbox →
        </Link>
      </p>

      {snaptradeAwaitReturn && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-soft)] p-4 text-sm">
          <span>
            Returned from the SnapTrade portal? Fetch your newly-connected
            brokerages.
          </span>
          <Button
            type="button"
            onClick={() => snaptradeExchangeMutation.mutate()}
            disabled={snaptradeExchangeMutation.isPending}
          >
            {snaptradeExchangeMutation.isPending
              ? "Fetching..."
              : "Fetch brokerages"}
          </Button>
        </div>
      )}

      {plaidLink.error && (
        <p className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-950 dark:text-red-200">
          Plaid Link failed to load: {plaidLink.error}
        </p>
      )}

      {isLoading && <p className="text-sm">Loading connections...</p>}
      {error && (
        <p className="text-sm text-red-700 dark:text-red-300">
          {error.message}
        </p>
      )}

      {!isLoading && connections.length === 0 ? (
        <EmptyState
          title="No connections yet"
          description="Connect a bank (Plaid) for daily-sync of transactions, or a brokerage (SnapTrade) for daily-sync of trades + dividends. Both are sandbox-only in v1; production access flips when approved."
        />
      ) : (
        <div className="space-y-4">
          {connections.map((c) => (
            <ConnectionRow
              key={c.connection_id}
              connection={c}
              onSync={(id) => syncMutation.mutate(id)}
              onDisconnect={(id) => disconnectMutation.mutate(id)}
              syncPending={syncMutation.isPending}
              disconnectPending={disconnectMutation.isPending}
            />
          ))}
        </div>
      )}

      <Toast message={toast.message} />
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.integrations} />
      </div>
    </div>
  );
}

function ConnectionRow({
  connection,
  onSync,
  onDisconnect,
  syncPending,
  disconnectPending,
}: {
  connection: Connection;
  onSync: (id: string) => void;
  onDisconnect: (id: string) => void;
  syncPending: boolean;
  disconnectPending: boolean;
}) {
  const tone =
    connection.status === "active"
      ? "success"
      : connection.status === "needs_reauth"
        ? "warning"
        : "neutral";
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{connection.institution_name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted)]">
            <Pill tone={tone}>{connection.status}</Pill>
            <span>{connection.provider}</span>
            <span>·</span>
            <span>
              {connection.last_successful_sync_at
                ? `Last sync ${connection.last_successful_sync_at.slice(0, 10)}`
                : "Never synced"}
            </span>
            {connection.last_error && (
              <span className="text-[var(--color-danger)]">
                · {connection.last_error}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onSync(connection.connection_id)}
            disabled={syncPending}
          >
            Sync now
          </Button>
          <ConfirmButton
            label="Disconnect"
            confirmLabel="Confirm disconnect"
            disabled={disconnectPending}
            onConfirm={() => onDisconnect(connection.connection_id)}
          />
        </div>
      </div>
    </Card>
  );
}
